// 10-step Event Integrity Score colour ramp (0–9 … 90–100), red → green.
// Mirrors `webui/app/src/pages/measurement/eisScale.ts` — duplicated here
// rather than imported because widgets cannot depend on app/src. Both read
// the same `--eis-0`…`--eis-9` design tokens, so the two stay visually
// identical without a shared import.
const EIS_STOPS = [
  "var(--eis-0)",
  "var(--eis-1)",
  "var(--eis-2)",
  "var(--eis-3)",
  "var(--eis-4)",
  "var(--eis-5)",
  "var(--eis-6)",
  "var(--eis-7)",
  "var(--eis-8)",
  "var(--eis-9)",
] as const;

/** Colour for an Event Integrity Score on the 10-step decile ramp. */
export function eisColor(score: number): string {
  const decile = Math.min(9, Math.max(0, Math.floor(score / 10)));
  return EIS_STOPS[decile];
}

interface EisBadgeProps {
  /** 0–100 Event Integrity Score. */
  score: number | null;
  size?: number;
  /**
   * Show the "EIS" caption under the score. By default it appears only when the
   * ring is large enough to hold it (`EIS_CAPTION_MIN_SIZE`); a small gauge shows
   * the score alone, centred, and names itself in its tooltip.
   */
  showCaption?: boolean;
  isAvg?: boolean;
}

/** Smallest ring diameter (px) whose inner circle holds the caption without touching the stroke. */
export const EIS_CAPTION_MIN_SIZE = 88;

/** Circular gauge badge for an Event Integrity Score, coloured by the EIS ramp. */
export function EisBadge({ score, size = 72, showCaption, isAvg = false }: EisBadgeProps) {
  const captionVisible = showCaption ?? size >= EIS_CAPTION_MIN_SIZE;
  const stroke = Math.max(4, Math.round(size * 0.1));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const available = score !== null && Number.isFinite(score);
  const clamped = available ? Math.max(0, Math.min(100, Math.round(score))) : 0;
  const dash = (clamped / 100) * circumference;
  const color = available ? eisColor(clamped) : "var(--text-subtle)";
  const name = isAvg ? "Average Event Integrity Score" : "Event Integrity Score";
  const label = available ? `${name} ${clamped}/100` : `${name} unavailable`;
  const center = size / 2;

  return (
    <div
      className="relative flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
      title={label}
    >
      <svg aria-hidden="true" width={size} height={size} className="-rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-border"
        />
        {available && clamped > 0 && <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
        />}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-semibold tabular-nums" style={{ color, fontSize: size * (captionVisible ? 0.34 : 0.38) }}>
          {available ? clamped : "—"}
        </span>
        {captionVisible && (
          <span
            className="mt-0.5 font-semibold text-muted"
            style={{ fontSize: Math.max(12, size * 0.11) }}
          >
            {isAvg ? "Avg EIS" : "EIS"}
          </span>
        )}
      </div>
    </div>
  );
}
